"use client";

import { type ReactNode, useEffect, useRef, useState } from "react";
import { LayoutImageDialog } from "../layout-image-dialog";
import { LayoutPreview, LayoutPreviewProvider } from "../layout-preview";
import { PracticeToolIcon } from "../practice-tool-icon";
import { DrillIcon } from "./icon";
import type { Drill } from "./types";
import { DrillVideo } from "./video";

export type DrillLayout = Drill["layout"];

export function LayoutCarousel({
  layouts,
  activeIndex,
  onActiveChange,
  actions,
  showDetails = true,
  emptySlide,
  expandable = false,
}: {
  layouts: DrillLayout[];
  activeIndex?: number;
  onActiveChange?: (index: number) => void;
  actions?: (index: number) => ReactNode;
  showDetails?: boolean;
  emptySlide?: ReactNode;
  expandable?: boolean;
}) {
  const [position, setPosition] = useState(0);
  const [enlarged, setEnlarged] = useState<{
    src: string;
    title: string;
    returnFocus: HTMLButtonElement;
  } | null>(null);
  const swipedAt = useRef(0);
  const start = useRef<{ x: number; y: number } | null>(null);
  const tabs = useRef<HTMLElement>(null);
  const slideCount = layouts.length + (emptySlide ? 1 : 0);
  const index = Math.max(0, Math.min(activeIndex ?? position, slideCount - 1));
  const layout = layouts[index];
  useEffect(() => {
    const container = tabs.current;
    const current = container?.querySelector<HTMLElement>(
      `button:nth-child(${index + 1})`,
    );
    if (!container || !current) return;
    if (current.offsetLeft < container.scrollLeft)
      container.scrollLeft = current.offsetLeft;
    else if (
      current.offsetLeft + current.offsetWidth >
      container.scrollLeft + container.clientWidth
    )
      container.scrollLeft =
        current.offsetLeft + current.offsetWidth - container.clientWidth;
  }, [index]);
  function go(next: number) {
    const value = Math.max(0, Math.min(next, slideCount - 1));
    setPosition(value);
    onActiveChange?.(value);
  }
  return (
    <section
      className="drill-layout-carousel"
      aria-label="配置図"
      aria-roledescription="カルーセル"
    >
      {layout || emptySlide ? (
        <>
          <div
            className="drill-layout-slide-heading"
            aria-live="polite"
            aria-atomic="true"
          >
            <span
              className={`drill-layout-badge${index > 0 ? " is-supporting" : ""}`}
            >
              {index === 0 ? "メイン配置図" : `サブ配置図 ${index}`}
            </span>
            <strong>
              {layout?.title ??
                (index === 0 ? "メイン配置図を登録" : "サブ配置図を追加")}
            </strong>
            <span className="drill-layout-position">
              {emptySlide
                ? `登録済み ${layouts.length}枚`
                : `${index + 1} / ${layouts.length}`}
            </span>
          </div>
          <section
            className="drill-layout-slide"
            aria-roledescription="スライド"
            aria-label={
              layout
                ? `${index + 1} / ${layouts.length} ${index === 0 ? "メイン配置図" : "サブ配置図"}`
                : index === 0
                  ? "メイン配置図を登録"
                  : "サブ配置図を追加"
            }
            onTouchStart={(event) => {
              const touch = event.touches[0];
              start.current = touch
                ? { x: touch.clientX, y: touch.clientY }
                : null;
            }}
            onTouchEnd={(event) => {
              const touch = event.changedTouches[0];
              const from = start.current;
              start.current = null;
              if (!from || !touch) return;
              const dx = touch.clientX - from.x;
              const dy = touch.clientY - from.y;
              if (Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy) * 1.5) {
                swipedAt.current = Date.now();
                go(index + (dx < 0 ? 1 : -1));
              }
            }}
          >
            {layout ? (
              <LayoutPreviewProvider>
                <LayoutPreview
                  key={layout.id}
                  {...layout}
                  href={null}
                  onEnlarge={
                    expandable
                      ? (src, returnFocus) => {
                          if (Date.now() - swipedAt.current < 500) return;
                          setEnlarged({
                            src,
                            title: layout.title,
                            returnFocus,
                          });
                        }
                      : undefined
                  }
                />
              </LayoutPreviewProvider>
            ) : (
              emptySlide
            )}
          </section>
          {slideCount > 1 && (
            <div className="drill-layout-navigation">
              <button
                type="button"
                className="drill-layout-arrow"
                aria-label="前の配置図"
                disabled={index === 0}
                onClick={() => go(index - 1)}
              >
                <DrillIcon name="back" size={18} />
              </button>
              <nav
                ref={tabs}
                className="drill-layout-tabs"
                aria-label="表示する配置図"
              >
                {layouts.map((item, position) => (
                  <button
                    type="button"
                    key={item.id}
                    aria-label={`${position === 0 ? "メイン配置図" : `サブ配置図 ${position}`}を表示`}
                    aria-current={index === position ? "true" : undefined}
                    onClick={() => go(position)}
                  >
                    <span aria-hidden="true" />
                    {position === 0 ? "メイン" : `サブ ${position}`}
                  </button>
                ))}
                {emptySlide && (
                  <button
                    type="button"
                    aria-label="サブ配置図を追加"
                    aria-current={index === layouts.length ? "true" : undefined}
                    onClick={() => go(layouts.length)}
                  >
                    <DrillIcon name="plus" size={13} />
                    追加
                  </button>
                )}
              </nav>
              <button
                type="button"
                className="drill-layout-arrow"
                aria-label="次の配置図"
                disabled={index === slideCount - 1}
                onClick={() => go(index + 1)}
              >
                <DrillIcon name="arrow" size={18} />
              </button>
            </div>
          )}
          {layout && actions?.(index)}
          {layout && showDetails && (layout.description || layout.videoUrl) && (
            <div
              className="drill-layout-details"
              aria-live="polite"
              aria-atomic="true"
            >
              {layout.description && <p>{layout.description}</p>}
              {layout.videoUrl && (
                <DrillVideo
                  key={`${layout.id}:${layout.videoUrl}`}
                  url={layout.videoUrl}
                  title={layout.title}
                />
              )}
            </div>
          )}
        </>
      ) : (
        <div className="drill-layout-placeholder">
          <PracticeToolIcon name="layouts" size={36} />
          <strong>練習する配置図を登録しましょう</strong>
          <p>最初の1枚がメイン配置図になります。</p>
        </div>
      )}
      {enlarged && (
        <LayoutImageDialog {...enlarged} onClose={() => setEnlarged(null)} />
      )}
    </section>
  );
}
